import {
  Component,
  Injector,
  Input,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import {
  Contact,
  Contractor,
  DraftStatus,
  InkFormData,
  InspectionAerosolShutdown,
  InspectionAirConditioningAndVentilationSystems,
  InspectionDesign,
  InspectionDto,
  InspectionFireAlarmSystem,
  InspectionFireExtinguishers,
  InspectionFireTaps,
  InspectionGasExtinguishingSystem,
  InspectionInstallation,
  InspectionIntegration,
  InspectionModel,
  InspectionPressureSystemInStairwellsCompatibilityCheck,
  InspectionTemplate,
  InspectionVentilationSystemAndFireProtection,
  Order,
  OrderStatus,
  Planner,
  RecognitionResult,
  SubProject,
  SummaryNotes,
  TextBank,
  User,
  WaterNetworkCharacterization
} from '@authority-labs/api-interfaces';
import { PERMISSION_KEYS } from '@authority-labs/constants';
import {
  Conclusion,
  InspectionProperty,
  InspectionStandardDescriptionMap,
  InspectionTypeEnum,
  InspectionTypes,
  OrderStatusEnum,
  Path
} from '@authority-labs/enums';
import {
  canTesterChangeStatusOf,
  getInspectionTypeTextBankKey,
  isTesterRole,
  nameof,
  TESTER_ALLOWED_ORDER_STATUSES,
  testerTargetStatuses
} from '@authority-labs/helpers';
import {
  UploadDirectory,
  UploadEntity,
  UploadFile
} from '@check-list/app/classes';
import { TextBankService } from '@check-list/app/shared';
import { CertificateDialogService } from '@check-list/app/shared/dialogs/certificate-dialog/certificate-dialog.service';
import { NzModalConfirmActionComponent } from '@check-list/app/shared/dialogs/confirm-action-dialog/confirm-action-dialog.component';
import { NotesBankDialogService } from '@check-list/app/shared/dialogs/notes-bank-dialog/notes-bank-dialog.service';
import { ConfigurationService } from '@check-list/app/shared/services/configuration/configuration.service';
import { FilesService } from '@check-list/app/shared/services/files/files.service';
import { ChangeType } from '@check-list/core/enums';
import { RequestQueryBuilder } from '@nestjsx/crud-request';
import format from 'date-fns/format';
import { he } from 'date-fns/locale';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { Observable } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
import { CrudDetailsComponent } from '../../../classes/crud-details.component';
import { inspectionDraftKey } from '../../../auth/inspection-draft-cache';
import { NavbarLink } from '../../../classes/models/link.model';
import { InspectionTemplatesService } from '../../inspection-templates/inspection-templates.service';
import { SelectInspectionTemplateDialogService } from '../../inspection-templates/select-inspection-template-dialog/select-inspection-template-dialogdialog.service';
import { OrderNotesService } from '../../orders/order-notes.service';
import { OrdersService } from '../../orders/orders.service';
import { UsersService } from '../../users/users.service';
import {
  DraftRecognitionDialogComponent,
  DraftRecognitionDialogResult
} from '../draft-recognition-dialog/draft-recognition-dialog.component';
import { InspectionDataService } from '../inspection-data.service';
import { InspectionPrintPreviewDialogComponent } from '../inspection-print-preview-dialog/inspection-print-preview-dialog.component';
import { InspectionsService } from '../inspections.service';
import { SelectInspectionDialogService } from '../select-inspection-dialog/select-inspection-dialog.service';
import { isInspectionReadonly } from '../shared/inspection-utils';

const PATH = 'text-bank';

/** Cache-only flag: the cached order carries unsaved edits. */
const CACHED_ORDER_CHANGED = '__orderDetailsChanged';

@Component({
  selector: 'authority-labs-inspection',
  templateUrl: './inspection-details.component.html',
  styleUrls: ['./inspection-details.component.scss'],
  providers: [InspectionDataService]
})
export class InspectionDetailsComponent extends CrudDetailsComponent<InspectionDto> {
  @Input() entity: InspectionModel;
  @Input() printPreview = false;

  @ViewChild('inspectionDesignTpl', { static: false })
  inspectionDesignTpl: TemplateRef<unknown>;
  @ViewChild('inspectionInstallationTpl', { static: false })
  inspectionInstallationTpl: TemplateRef<unknown>;
  @ViewChild('inspectionWaterNetworkCharacterizationTpl', { static: false })
  inspectionWaterNetworkCharacterizationTpl: TemplateRef<unknown>;
  @ViewChild('inspectionGasExtinguishingSystemTpl', { static: false })
  inspectionGasExtinguishingSystemTpl: TemplateRef<unknown>;
  @ViewChild('inspectionFireTapsTpl', { static: false })
  inspectionFireTapsTpl: TemplateRef<unknown>;
  @ViewChild('inspectionFireExtinguishersTpl', { static: false })
  inspectionFireExtinguishersTpl: TemplateRef<unknown>;
  @ViewChild('inspectionAerosolShutdownTpl', { static: false })
  inspectionAerosolShutdownTpl: TemplateRef<unknown>;
  @ViewChild('inspectionFireAlarmSystemTpl', { static: false })
  inspectionFireAlarmSystemTpl: TemplateRef<unknown>;
  @ViewChild('inspectionAirConditioningAndVentilationSystemsTpl', {
    static: false
  })
  inspectionAirConditioningAndVentilationSystemsTpl: TemplateRef<unknown>;
  @ViewChild('inspectionVentilationSystemAndFireProtectionTpl', {
    static: false
  })
  inspectionVentilationSystemAndFireProtectionTpl: TemplateRef<unknown>;
  @ViewChild('inspectionPressureSystemInStairwellsCompatibilityCheckTpl', {
    static: false
  })
  inspectionPressureSystemInStairwellsCompatibilityCheckTpl: TemplateRef<unknown>;
  @ViewChild('inspectionIntegrationTpl', { static: false })
  inspectionIntegrationTpl: TemplateRef<unknown>;

  navbarLinks: NavbarLink[] = [];
  dataChanged = false;
  submitting = false;
  contractor: Contractor;
  selectedTabIndex = 0;
  isMobile = false;
  fireExtinguishingType = ['מתזים / קצף', 'מתזים', 'קצף'];
  standards = [];
  inspectionTypes: InspectionTypeEnum[];
  orderStatusList: OrderStatus[];
  managers: User[] = [];
  orderDetailsChanged = false;
  drawerOpened = true;
  conclusions = Conclusion;
  NotApplicable = Conclusion.NotApplicable;
  isManualConclusion = false;
  initInspectionInProgress = false;
  uploadEntity: UploadEntity;
  dateNow: string;
  draftInkData: InkFormData;
  draftInspectionData: InspectionDto;
  draftStatus: DraftStatus;

  private timeoutRef: NodeJS.Timeout;

  tabItems: {
    inspectionKey: InspectionProperty;
    title: string;
    template: TemplateRef<unknown>;
  }[] = [];

  get conclusion(): Conclusion {
    return this.entity.isPassed ? Conclusion.Pass : Conclusion.Fail;
  }

  get tabColor(): string {
    return this.entity && this.entity.isPassed ? 'primary' : 'warn';
  }

  get title(): string {
    return `הזמנה מס' ${this.entity.orderId} ( ${
      this.entity.order?.subProject?.name || ''
    } )`;
  }

  get drawerMode(): string {
    return this.isMobile ? 'over' : 'side';
  }

  get isManager(): boolean {
    return this.appPermissionsService.has(PERMISSION_KEYS.INSPECTIONS_MANAGE);
  }

  get isTester(): boolean {
    return this.appPermissionsService.has(PERMISSION_KEYS.INSPECTIONS_EDIT);
  }

  /** The logged-in user holds the tester / senior-tester role. */
  get isTesterUser(): boolean {
    return isTesterRole(this.authService.getUser()?.role);
  }

  /**
   * A tester may only touch the status of an order assigned to them, and only
   * while it sits on a status they own - once it waits for an approver it is
   * locked for them. Reads the persisted order rather than the
   * edited one so the check can't be unlocked from the form itself.
   */
  get canTesterEditStatus(): boolean {
    const order = this.origEntity?.order;

    return canTesterChangeStatusOf({
      assignee: order?.assignee,
      reviewedBy: order?.reviewedBy,
      username: this.authService.getUser()?.username,
      currentStatusKey: order?.status?.key
    });
  }

  /** Whether the order-status select on the checklist screen is locked. */
  get isOrderStatusSelectDisabled(): boolean {
    return this.isTesterUser && !this.canTesterEditStatus;
  }

  get canShooseInspection(): boolean {
    const inspectionTypes = [
      InspectionTypeEnum.InspectionDesign,
      InspectionTypeEnum.InspectionInstallation,
      InspectionTypeEnum.InspectionDesignAndInstallation
    ];
    return (
      !this.entity.inspectionType ||
      inspectionTypes.includes(this.entity.inspectionType)
    );
  }

  get inspectionTypeTextBankKey(): string {
    return getInspectionTypeTextBankKey(this.entity?.inspectionType);
  }

  get subFolders(): Record<string, string> {
    const storage = this.entity?.order?.sharePoint || {};
    return {
      plans: storage.plans
    };
  }

  constructor(
    injector: Injector,
    private service: InspectionsService,
    private ordersService: OrdersService,
    private configService: ConfigurationService,
    private usersService: UsersService,
    public notesService: OrderNotesService,
    private readonly notesBankDialogService: NotesBankDialogService,
    private readonly textBankService: TextBankService,
    private selectInspectionDialog: SelectInspectionDialogService,
    private selectInspectionTemplateDialog: SelectInspectionTemplateDialogService,
    private filesService: FilesService,
    private certificateDialog: CertificateDialogService,
    private dialog: MatDialog,
    private inspectionTemplatesService: InspectionTemplatesService,
    private inspectionDataService: InspectionDataService
  ) {
    super(injector, service);

    this.dateNow = format(new Date(), 'PPP', {
      locale: he
    });

    this.layoutService.isMobile.subscribe((isMobile) => {
      this.isMobile = isMobile;
    });

    this.orderStatusList = this.configService.orderStatus;
    this.managers = this.usersService.getManagers();

    TextBankService.changesEmitter
      .pipe(
        takeUntil(this.componentDestroyed$),
        filter((event) => event.path === PATH)
      )
      .subscribe((event) => {
        if (
          [ChangeType.deleted, ChangeType.edited, ChangeType.created].includes(
            event.action
          )
        ) {
          this.loadStandards();
        }
      });
  }

  compareFn = (o1: { id: number | string }, o2: { id: number | string }) =>
    o1 && o2 ? o1.id === o2.id : o1 === o2;

  canDeactivate(): Observable<boolean> | boolean {
    return !this.dataChanged;
  }

  toggleDrawer(): void {
    this.drawerOpened = !this.drawerOpened;
  }

  save(): void {
    if (this.readonly || this.printPreview) {
      return;
    }

    // An autosave firing mid-save would resend the same version and get a
    // false 409; the next change schedules a fresh autosave.
    if (this.submitting) {
      this.timeoutRef = null;
      return;
    }

    clearTimeout(this.timeoutRef);
    this.timeoutRef = null;
    this.submitting = true;

    if (
      !this.isManualConclusion &&
      this.entity.order.conclusion !== Conclusion.NoConclusion &&
      this.entity.order.conclusion !== this.conclusion
    ) {
      this.entity.order.conclusion = this.conclusion;
      this.orderDetailsChanged = true;
    }

    // The inspection goes first: the order save may move the order to a
    // status in which the inspection is readonly, and the inspection save
    // would then be refused and this session's edits lost.
    const pendingOrder = this.orderDetailsChanged ? this.entity.order : null;
    this.update().subscribe({
      next: () => pendingOrder && this.saveOrder(pendingOrder),
      error: this.onUpdateError
    });
  }

  private saveOrder(order: Order): void {
    this.submitting = true;
    this.ordersService.updateOne(this.entity.orderId, order).subscribe({
      next: (saved) => {
        this.entity.order = { ...order, ...saved };
        this.readonly = this.isReadonly();
        this.submitting = false;
        this.saveCurrentState();
        this.setNavbarLinks();
      },
      error: (error) => {
        // The inspection is saved; keep the order edits so they can be retried.
        this.entity.order = order;
        this.orderDetailsChanged = true;
        this.dataValChanged(true);
        this.writeCache();
        this.onError(error);
      }
    });
  }

  cancel(): void {
    super.cancel();
    this.clearCache();

    if (this.orderDetailsChanged) {
      this.setTabs();
    }
    this.orderDetailsChanged = false;

    this.setNavbarLinks();
    this.dataValChanged(false);
  }

  onOrderDetailsChange() {
    this.orderDetailsChanged = true;
    this.onDataChange();
  }

  onSummaryNotesChange(
    summaryNotes: SummaryNotes,
    entity: { summaryNotes: SummaryNotes }
  ): void {
    entity.summaryNotes = summaryNotes;
    this.onDataChange();
  }

  onDataChange(): void {
    if (this.readonly || this.printPreview) {
      return;
    }

    this.entity.setInspectionStatus();
    this.setNavbarLinks();

    this.writeCache();
    this.dataValChanged(true);

    if (!this.timeoutRef) {
      this.timeoutRef = setTimeout(this.save.bind(this), 5 * 60 * 1000);
    }
  }

  plannerSelected(planner: Planner): void {
    this.entity.order.planner = planner;
    this.onOrderDetailsChange();
  }

  safetyConsultantSelected(planner: Planner): void {
    this.entity.inspectionIntegration.integrationDesign.safetyConsultant =
      planner;
    this.onDataChange();
  }

  contractorSelected(contractor: Contractor): void {
    this.entity.order.contractor = contractor;
    this.onOrderDetailsChange();
  }

  init(inspection: InspectionModel): void {
    this.entity = inspection;

    this.readonly = this.isReadonly();
    this.printPreview = this.printPreview || !!this.queryParams.printPreview;

    this.entity.order.planner = this.entity.order.planner ?? new Planner();
    this.entity.order.contractor =
      this.entity.order.contractor ?? new Contractor();
    this.entity.summaryNotes = Array.isArray(this.entity.summaryNotes)
      ? this.entity.summaryNotes
      : [];

    this.setInspectionType(this.entity.inspectionType);
    this.loadStandards();
    this.uploadEntity = new UploadEntity(Path.Orders, this.entity.order.id);

    // Load draft ink data for draft field indicators (skip for new/preview)
    if (this.entity.id && !this.printPreview) {
      this.service
        .getDraftData(this.entity.id)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe((resp) => {
          this.draftInkData = resp.inkFormData;
          this.draftInspectionData = resp.inspectionData;
          this.draftStatus = resp.status;
          this.inspectionDataService.setDraftInkData(resp.inkFormData);
        });
    }
  }

  afterInit(): void {
    this.setTabs();
    this.setNavbarLinks();
    if (!(this.readonly || this.printPreview)) {
      const cached = this.readCache();
      if (cached) {
        const { [CACHED_ORDER_CHANGED]: cachedOrderChanged, ...inspectionDto } =
          cached;

        // The cache holds the whole inspection; restoring one taken from an
        // older server copy would overwrite everything saved since.
        if (!this.isCacheCurrent(inspectionDto)) {
          this.clearCache();
          this.modal.warning({
            nzTitle: 'שינויים מקומיים ישנים נמחקו',
            nzContent:
              'נמצאו שינויים שלא נשמרו מגרסה קודמת של הבדיקה. הבדיקה עודכנה מאז, ולכן לא ניתן לטעון אותם בבטחה.'
          });
          return;
        }

        this.modal.confirm({
          nzTitle: 'שינויים ממתינים',
          nzContent: 'האם לטעון שינויים שלא נשמרו?',
          nzOnOk: () => this.restoreCache(inspectionDto, !!cachedOrderChanged),
          nzOnCancel: () => this.clearCache()
        });
      }
    }
  }

  createNewEntity(queryParams?: Record<string, string>): void {
    this.entity = new InspectionModel({});
    this._initInspection();

    const inspectionType: InspectionTypeEnum = Object.values(
      InspectionTypeEnum
    ).includes(queryParams.inspectionType as InspectionTypeEnum)
      ? (queryParams.inspectionType as InspectionTypeEnum)
      : InspectionTypeEnum.InspectionDesign;

    this.entity.inspectionType = inspectionType;
    this.entity.order.sharePoint = {};
    this.init(this.entity);
  }

  setStandard = (value: string) => (this.entity.standard = value);
  setStandardExtraText = (value: string) =>
    (this.entity.standardExtraText = value);
  setInspectionCount = (value: string) => (this.entity.inspectionCount = value);

  showNotesBank(
    e: Event,
    type: string,
    callback: (value: string) => void,
    hasDefaults: boolean = false
  ): void {
    e.stopPropagation();
    this.notesBankDialogService
      .show(type, hasDefaults)
      .afterClosed()
      .subscribe((selected) => {
        if (selected) {
          callback(selected);
          this.onDataChange();
        }
      });
  }

  loadStandards(): void {
    const qb = RequestQueryBuilder.create();
    qb.search({
      [nameof<TextBank>('type')]: {
        $eq: this.inspectionTypeTextBankKey
      }
    });

    this.textBankService
      .getAll(qb)
      .pipe(map((resp) => resp.map((item) => item.content)))
      .subscribe((resp) => (this.standards = resp));
  }

  conclusionChanged(select: MatSelectChange): void {
    this.modal.confirm({
      nzTitle: `האם אתה בטוח שברצונך לבחור במסקנה "${select.value}?"`,
      nzOnOk: () => (this.isManualConclusion = true),
      nzOnCancel: () => (this.entity.order.conclusion = this.conclusion)
    });
    this.onOrderDetailsChange();
  }

  onInspectionTypeChange(select: MatSelectChange): void {
    this.entity.order.inspectionType = select.value;
    this.orderDetailsChanged = true;

    if (this.isNew) {
      this._initInspection();
    }
    this.setInspectionType(select.value);
    this.setTabs();
    this.onDataChange();
  }

  extendInspection(): void {
    this.selectInspectionDialog
      .show(
        this.entity.order.id,
        this.entity.order.subProjectId,
        this.entity.order.projectId,
        this.entity.inspectionType
      )
      .afterClosed()
      .subscribe((inspectionId) => {
        if (inspectionId) {
          const query = RequestQueryBuilder.create({
            join: this.extraParams
          }).query();

          this.service.getOne(inspectionId, query).subscribe((inspection) => {
            // this.entity.inspectionType = inspection.inspectionType;
            this.entity.standard = inspection.standard;
            this.entity.inspectionCount = inspection.inspectionCount;
            this.entity.summaryNotes = inspection.summaryNotes;

            InspectionTypes.forEach(
              (inspectionType) =>
                (this.entity[inspectionType] = inspection[inspectionType])
            );

            if (inspection.order?.planner) {
              this.plannerSelected(inspection.order.planner);
            }
            this.entity.setInspectionStatus();
            this.loadStandards();
            this.onDataChange();
          });
        }
      });
  }

  extendInspectionFromTemplate(): void {
    this.selectInspectionTemplateDialog
      .show(this.entity.inspectionType)
      .afterClosed()
      .subscribe((inspectionTemplate) => {
        if (inspectionTemplate?.inspection) {
          this.entity.inspectionType =
            inspectionTemplate.inspection.inspectionType;

          InspectionTypes.forEach(
            (inspectionType) =>
              (this.entity[inspectionType] =
                inspectionTemplate.inspection[inspectionType])
          );

          this.entity.setInspectionStatus();
          this.loadStandards();
          this.onDataChange();
        }
      });
  }

  initInspection(): void {
    this.modal.confirm({
      nzTitle: 'איפוס נתונים',
      nzContent: 'האם את/ה בטוח/ה שברצונך לאפס את הנתונים?',
      nzOnOk: () => {
        this._initInspection();
        this.setTabs();
        this.onDataChange();
      }
    });
  }

  beforeUpload = (file?: NzUploadFile): boolean => {
    const uploadDirectory = new UploadDirectory({
      namePath: 'checkList',
      uploadEntity: { id: this.entity.orderId, type: Path.Orders },
      id: this.entity.order.sharePoint?.checkList
    });

    const uploadFile = new UploadFile(file, uploadDirectory);
    this.filesService.upload([uploadFile]);

    return false;
  };

  openCertificateDialog(): void {
    const query = RequestQueryBuilder.create({
      fields: [
        'createdAt',
        'assignee',
        'approvedBy',
        'version',
        'items',
        'items.inspection',
        'inspection',
        'subProject',
        'subProject.company',
        'planner',
        'contacts',
        'contractor',
        'isApproved'
      ],
      join: [
        {
          field: 'subProject',
          select: [
            nameof<SubProject>('block'),
            nameof<SubProject>('lot'),
            nameof<SubProject>('name'),
            nameof<SubProject>('plot')
          ]
        },
        { field: 'subProject.project' },
        { field: 'subProject.company' },
        { field: 'subProject.project.city' },
        { field: 'planner' },
        { field: 'contacts' },
        { field: 'contractor' },
        { field: 'items', select: ['id'] },
        {
          field: 'items.inspection',
          select: ['id', 'name']
        }
      ]
    }).query();

    this.ordersService.getOne(this.entity.orderId, query).subscribe((order) => {
      const mainContact = order.mainContactId
        ? order.contacts.find((c) => c.id === order.mainContactId)
        : undefined;
      const contact = mainContact ?? order.contacts[0] ?? new Contact();

      this.certificateDialog.showInvoice(
        {
          order,
          inspection: this.entity,
          planner: order.planner,
          contractor: order.contractor,
          project: order.subProject.project,
          subProject: order.subProject,
          company: order.subProject.company,
          contact
        },
        true
      );
    });
  }

  selectedTabIndexChange(index: number) {
    if (index !== 0 && !this.entity.inspectionDate) {
      this.modal.warning({
        nzTitle: 'תאריך הבדיקה לא נבחר',
        nzContent: 'לא לשכוח למלא את תאריך הבדיקה!'
      });
    }

    this.setNavbarLinks();
  }

  getUserSignatureUrl(username: string): string {
    const assignee = this.usersService.users.find(
      (user) => user.username === username
    );

    return assignee?.signature;
  }

  inspectionPrintPreview(): void {
    const dialogConfig: MatDialogConfig = new MatDialogConfig();
    dialogConfig.minWidth = '1200px';
    dialogConfig.data = this.entity.id;
    this.dialog.open(InspectionPrintPreviewDialogComponent, dialogConfig);
  }

  openDraft(): void {
    this.update().subscribe(() => {
      this.router.navigate(['/app/inspections', this.entity.id, 'draft']);
    });
  }

  importFromDraft(): void {
    const dialogRef = this.dialog.open(DraftRecognitionDialogComponent, {
      data: { inspectionId: this.entity.id },
      width: '95vw',
      maxWidth: '1200px',
      height: '90vh',
      disableClose: true
    });

    dialogRef
      .afterClosed()
      .subscribe((result: DraftRecognitionDialogResult) => {
        if (!result) return; // cancelled

        this.applyDraftData();

        if (result.accepted?.length) {
          this.applyRecognitionResults(result.accepted);
        }
      });
  }

  private applyDraftData(): void {
    const order = this.entity.order;
    this.entity = new InspectionModel(this.draftInspectionData);
    this.entity.order = order;
    this.setInspectionType(this.entity.inspectionType);
    this.entity.setInspectionStatus();
    this.loadStandards();
    this.setTabs();
    this.setNavbarLinks();
    this.onDataChange();
  }

  private applyRecognitionResults(results: RecognitionResult[]): void {
    let appliedCount = 0;

    for (const result of results) {
      if (result.rawText === '???') continue;

      if (result.notePath) {
        const applied = this.applyInkNoteResult(result);
        if (applied) appliedCount++;
        continue;
      }

      if (result.typedValue == null) continue;
      const applied = this.applyFieldResult(result);
      if (applied) appliedCount++;
    }

    if (appliedCount > 0) {
      this.onDataChange();
      this.message.create('success', `הוזנו ${appliedCount} שדות בהצלחה`);
    }
  }

  private applyFieldResult(result: RecognitionResult): boolean {
    try {
      const parts = result.fieldId.split(':');
      const section = parts[0];

      // general:fieldName → entity.fieldName
      if (section === 'general') {
        const fieldName = parts[1];
        if (fieldName && fieldName in this.entity) {
          this.entity[fieldName] = result.typedValue;
          return true;
        }
        return false;
      }

      // waterNetwork:index:fieldName → entity.inspectionWaterNetworkCharacterization.testData[index].fieldName
      if (section === 'waterNetwork' && parts.length >= 3) {
        const index = parseInt(parts[1], 10);
        const fieldName = parts[2];
        const testData =
          this.entity.inspectionWaterNetworkCharacterization?.testData;
        if (testData && testData[index] && fieldName in testData[index]) {
          testData[index][fieldName] = result.typedValue;
          return true;
        }
        return false;
      }

      // waterFlow:subKey:fieldName → entity.waterFlowPressure.{mappedSubKey}.fieldName
      if (section === 'waterFlow' && parts.length >= 3) {
        const subKeyMap: Record<string, string> = {
          closedTap: 'flowAndPressureClosedTap',
          '100': 'flowAndPressure100',
          '150': 'flowAndPressure150'
        };
        const subKey = subKeyMap[parts[1]];
        const fieldName = parts[2];
        const waterFlow = this.entity.waterFlowPressure;
        if (
          waterFlow &&
          subKey &&
          waterFlow[subKey] &&
          fieldName in waterFlow[subKey]
        ) {
          waterFlow[subKey][fieldName] = result.typedValue;
          return true;
        }
        return false;
      }

      // Section mapping for known inspection types
      const sectionToEntityKey: Record<string, string> = {
        installation: 'inspectionInstallation',
        alarms: 'inspectionFireAlarmSystem',
        design: 'inspectionDesign',
        gasExtinguishing: 'inspectionGasExtinguishingSystem',
        fireTaps: 'inspectionFireTaps',
        fireExtinguishers: 'inspectionFireExtinguishers',
        aerosolShutdown: 'inspectionAerosolShutdown',
        fireAlarmSystem: 'inspectionFireAlarmSystem',
        airConditioningVentilation:
          'inspectionAirConditioningAndVentilationSystems',
        ventilationSystemFireProtection:
          'inspectionVentilationSystemAndFireProtection',
        pressureSystemStairwells:
          'inspectionPressureSystemInStairwellsCompatibilityCheck',
        integration: 'inspectionIntegration'
      };

      const entityKey = sectionToEntityKey[section];
      const inspectionObj = entityKey ? this.entity[entityKey] : null;

      // Known section with direct entity mapping
      if (inspectionObj) {
        // section:fieldName → entity[entityKey].fieldName
        if (parts.length === 2) {
          const fieldName = parts[1];
          if (fieldName in inspectionObj) {
            inspectionObj[fieldName] = result.typedValue;
            return true;
          }
          return false;
        }

        // design:plans:{guid}:fieldName → entity[entityKey].planningDetails.plans[guid].fieldName
        if (parts.length >= 4 && parts[1] === 'plans') {
          const guid = parts[2];
          const fieldName = parts[3];
          const plans = inspectionObj.planningDetails?.plans;
          if (Array.isArray(plans)) {
            const plan = plans.find((p) => p.guid === guid);
            if (plan && fieldName in plan) {
              plan[fieldName] = result.typedValue;
              return true;
            }
          }
          return false;
        }

        // Try known nested patterns within the inspection object
        if (this.applyNestedField(inspectionObj, parts.slice(1), result)) {
          return true;
        }
      }

      // Fallback: deep search the entity tree by title matching
      // Handles fields with Hebrew titles as section keys (e.g. risk levels, capacity)
      return this.applyFieldByDeepSearch(parts, result);
    } catch {
      return false;
    }
  }

  /**
   * Try known nested patterns within an inspection sub-object.
   * Handles: risk levels, contacts, case title lookups, index-based arrays.
   */
  private applyNestedField(
    obj: any,
    remainingParts: string[],
    result: RecognitionResult
  ): boolean {
    if (!obj || remainingParts.length < 2) return false;

    const [keyOrTitle, ...rest] = remainingParts;

    // risk:level → riskLevels[level].installationArea
    if (keyOrTitle === 'risk' && rest.length >= 1) {
      const level = rest[0];
      if (Array.isArray(obj.riskLevels)) {
        const rl = obj.riskLevels.find((r) => r.level === level);
        if (rl) {
          rl.installationArea = result.typedValue;
          return true;
        }
      }
      return false;
    }

    // contact:index:fieldName → contacts[index].fieldName
    if (keyOrTitle === 'contact' && rest.length >= 2) {
      const idx = parseInt(rest[0], 10);
      const fieldName = rest[1];
      if (obj.contacts?.[idx]) {
        obj.contacts[idx][fieldName] = result.typedValue;
        return true;
      }
      return false;
    }

    // Direct property access
    if (rest.length === 0 && keyOrTitle in obj) {
      obj[keyOrTitle] = result.typedValue;
      return true;
    }

    // Numeric index into arrays (cases, testData)
    const idx = parseInt(keyOrTitle, 10);
    if (!isNaN(idx)) {
      for (const prop of ['cases', 'testData']) {
        if (Array.isArray(obj[prop]) && obj[prop][idx]) {
          if (this.applyNestedField(obj[prop][idx], rest, result)) return true;
        }
      }
      return false;
    }

    // String key: try direct property, then title-based search in arrays
    if (obj[keyOrTitle] && typeof obj[keyOrTitle] === 'object') {
      if (this.applyNestedField(obj[keyOrTitle], rest, result)) return true;
    }

    for (const prop of ['cases', 'testData']) {
      if (Array.isArray(obj[prop])) {
        const match = obj[prop].find((c) => c.title === keyOrTitle);
        if (match && this.applyNestedField(match, rest, result)) return true;
      }
    }

    return false;
  }

  /**
   * Fallback: deep search the entire entity for objects matching by title.
   * Used for fields with Hebrew titles as section keys (e.g. risk levels).
   */
  private applyFieldByDeepSearch(
    parts: string[],
    result: RecognitionResult
  ): boolean {
    const visited = new Set<any>();

    const search = (obj: any, depth: number): boolean => {
      if (!obj || typeof obj !== 'object' || depth > 8 || visited.has(obj)) {
        return false;
      }
      visited.add(obj);

      // Check if this object's title matches the first segment
      if (obj.title === parts[0]) {
        if (this.applyNestedField(obj, parts.slice(1), result)) return true;
      }

      // Recurse into properties
      for (const key of Object.keys(obj)) {
        const val = obj[key];
        if (Array.isArray(val)) {
          for (const item of val) {
            if (search(item, depth + 1)) return true;
          }
        } else if (val && typeof val === 'object') {
          if (search(val, depth + 1)) return true;
        }
      }
      return false;
    };

    return search(this.entity, 0);
  }

  private applyInkNoteResult(result: RecognitionResult): boolean {
    try {
      const path = result.notePath.split('.');
      let obj = this.entity;

      for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];
        obj = isNaN(+key) ? obj[key] : obj[+key];
        if (obj == null) return false;
      }

      const noteIndex = +path[path.length - 1];
      if (obj[noteIndex] && typeof obj[noteIndex].notes === 'string') {
        obj[noteIndex].notes = result.rawText;
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  saveAsTemplate(): void {
    const title = 'יצירת תבנית';
    const subTitle = `האם את/ה בטוח/ה שברצונך ליצור את התבנית עבור הצ'קליסט הנוכחי?`;

    this.modal.create({
      nzClosable: false,
      nzContent: NzModalConfirmActionComponent,
      nzComponentParams: {
        title,
        subTitle
      },
      nzOnOk: () => this.createTemplate()
    });
  }

  canUseStatus(status: OrderStatus): boolean {
    if (this.isTesterUser) {
      return (
        this.canTesterEditStatus &&
        testerTargetStatuses(this.origEntity?.order?.status?.key).includes(
          status.key as OrderStatusEnum
        )
      );
    }

    return (
      this.isManager ||
      (this.isTester &&
        TESTER_ALLOWED_ORDER_STATUSES.includes(status.key as OrderStatusEnum))
    );
  }

  private setTabs(): void {
    const tabItems = this.entity.getActiveInspectionsKeys().map((key) => ({
      inspectionKey: key as InspectionProperty,
      title: this.entity[key].title,
      template: this[`${key}Tpl`]
    }));

    this.tabItems.length = 0;
    tabItems.forEach((t) => this.tabItems.push(t));
  }

  private setNavbarLinks(): void {
    const inspection =
      this.entity[
        this.tabItems[Math.max(this.selectedTabIndex - 1, 0)]?.inspectionKey
      ];
    this.navbarLinks =
      inspection?.getCases().map((x) => ({
        title: x.title,
        sublinks: x.cases.map((item) => ({
          title: item.title,
          isPassed: !item.isApplicable || item.isPassed
        }))
      })) ?? [];
  }

  private createTemplate(): void {
    const inspection: InspectionModel = new InspectionModel({
      inspectionType: this.entity.inspectionType,
      standard: this.entity.standard,
      inspectionCount: this.entity.inspectionCount,
      summaryNotes: this.entity.summaryNotes
    });

    InspectionTypes.forEach(
      (inspectionType) =>
        (inspection[inspectionType] = this.entity[inspectionType])
    );

    const inspectionTemplate: InspectionTemplate = {
      name: this.entity.order?.subProject?.name || 'טמפלייט חדש',
      inspectionType: this.entity.inspectionType,
      description: '',
      inspection
    };

    this.inspectionTemplatesService
      .createOne(inspectionTemplate)
      .subscribe(this.onCreateTemplateSuccess, this.onError);
  }

  private setInspectionType(inspectionType: InspectionTypeEnum): void {
    switch (inspectionType) {
      case InspectionTypeEnum.InspectionDesign:
        this.inspectionTypes = [
          InspectionTypeEnum.InspectionDesign,
          InspectionTypeEnum.InspectionInstallation,
          InspectionTypeEnum.InspectionDesignAndInstallation
        ];
        this.clearInspections([
          'inspectionInstallation',
          'inspectionWaterNetworkCharacterization'
        ]);
        this.setInspectionDesign();
        break;
      case InspectionTypeEnum.InspectionInstallation:
      case InspectionTypeEnum.InspectionDesignAndInstallation:
        this.inspectionTypes = [
          InspectionTypeEnum.InspectionDesign,
          InspectionTypeEnum.InspectionInstallation,
          InspectionTypeEnum.InspectionDesignAndInstallation
        ];
        this.clearInspections(['inspectionWaterNetworkCharacterization']);
        this.setInspectionDesign();
        this.setInspectionInstallation();
        break;
      case InspectionTypeEnum.InspectionWaterNetworkCharacterization:
        this.inspectionTypes = [
          InspectionTypeEnum.InspectionWaterNetworkCharacterization
        ];
        this.entity.order.conclusion = Conclusion.NoConclusion;
        this.orderDetailsChanged = true;
        this.clearInspections(['inspectionDesign', 'inspectionInstallation']);
        this.setInspectionWaterNetworkCharacterization();
        break;
      case InspectionTypeEnum.InspectionGasExtinguishingSystem:
        this.setInspectionGasExtinguishingSystem();
        break;
      case InspectionTypeEnum.InspectionFireTaps:
        this.setInspectionnFireTaps();
        break;
      case InspectionTypeEnum.InspectionFireExtinguishers:
        this.setInspectionnFireExtinguishers();
        break;
      case InspectionTypeEnum.InspectionAerosolShutdown:
        this.setInspectionnAerosolShutdown();
        break;
      case InspectionTypeEnum.InspectionFireAlarmSystem:
        this.setInspectionnFireAlarmSystem();
        break;
      case InspectionTypeEnum.InspectionAirConditioningAndVentilationSystems:
        this.setInspectionAirConditioningAndVentilationSystems();
        break;
      case InspectionTypeEnum.InspectionVentilationSystemAndFireProtection:
        this.setInspectionVentilationSystemAndFireProtection();
        break;
      case InspectionTypeEnum.InspectionPressureSystemInStairwellsCompatibilityCheck:
        this.setInspectionPressureSystemInStairwellsCompatibilityCheck();
        break;
      case InspectionTypeEnum.InspectionIntegration:
        this.setInspectionIntegration();
        break;
      default:
        this.inspectionTypes = Object.values(InspectionTypeEnum);
        break;
    }

    if (this.isNew) {
      this.inspectionTypes = Object.values(InspectionTypeEnum);
      this.prepareForPreview();
    }

    const standardDescription = this.getInspectionStandardDescription();
    this.inspectionDataService.setStandardDescription(standardDescription);
  }

  private setInspectionDesign(): void {
    this.entity.inspectionDesign = new InspectionDesign(
      this.entity.inspectionDesign || undefined
    );
    this.entity.inspectionDesign.isApplicable = true;
  }

  private setInspectionInstallation(): void {
    this.entity.inspectionInstallation = new InspectionInstallation(
      this.entity.inspectionInstallation || undefined
    );
    this.entity.inspectionInstallation.isApplicable = true;

    const waterNetworkCharacterization =
      this.entity.inspectionInstallation?.waterNetworkCharacterization;

    if (
      waterNetworkCharacterization?.testData &&
      waterNetworkCharacterization?.testData.length === 1
    ) {
      waterNetworkCharacterization.testData[0].systemDemand =
        waterNetworkCharacterization.testData[0].systemDemand ??
        this.entity.waterFlowPressure.pressure;
      waterNetworkCharacterization.testData[0].systemFlow =
        waterNetworkCharacterization.testData[0].systemFlow ??
        this.entity.waterFlowPressure.flowRate;
      waterNetworkCharacterization.testData[0].testerName =
        waterNetworkCharacterization.testData[0].testerName ??
        this.usersService.getDisplayName(this.entity.order.assignee);
    }
  }

  private setInspectionWaterNetworkCharacterization(): void {
    const data = this.entity.inspectionWaterNetworkCharacterization || {};
    this.entity.inspectionWaterNetworkCharacterization =
      new WaterNetworkCharacterization({
        ...data,
        isApplicable: true
      });
    this.entity.inspectionWaterNetworkCharacterization.testData[0].testerName =
      this.entity.inspectionWaterNetworkCharacterization.testData[0]
        .testerName ??
      this.usersService.getDisplayName(this.entity.order.assignee);
  }

  private setInspectionGasExtinguishingSystem(): void {
    this.inspectionTypes = [
      InspectionTypeEnum.InspectionGasExtinguishingSystem
    ];
    this.entity.inspectionGasExtinguishingSystem =
      new InspectionGasExtinguishingSystem(
        this.entity.inspectionGasExtinguishingSystem || undefined
      );
  }

  private setInspectionnAerosolShutdown(): void {
    this.inspectionTypes = [InspectionTypeEnum.InspectionAerosolShutdown];
    this.entity.inspectionAerosolShutdown = new InspectionAerosolShutdown(
      this.entity.inspectionAerosolShutdown || undefined
    );
  }

  private setInspectionnFireTaps(): void {
    this.inspectionTypes = [InspectionTypeEnum.InspectionFireTaps];
    this.entity.inspectionFireTaps = new InspectionFireTaps(
      this.entity.inspectionFireTaps || undefined
    );
  }

  private setInspectionnFireAlarmSystem(): void {
    this.inspectionTypes = [InspectionTypeEnum.InspectionFireAlarmSystem];
    this.entity.inspectionFireAlarmSystem = new InspectionFireAlarmSystem(
      this.entity.inspectionFireAlarmSystem || undefined
    );
  }

  private setInspectionnFireExtinguishers(): void {
    this.inspectionTypes = [InspectionTypeEnum.InspectionFireExtinguishers];
    this.entity.inspectionFireExtinguishers = new InspectionFireExtinguishers(
      this.entity.inspectionFireExtinguishers || undefined
    );
  }

  private setInspectionAirConditioningAndVentilationSystems(): void {
    this.inspectionTypes = [
      InspectionTypeEnum.InspectionAirConditioningAndVentilationSystems
    ];
    this.entity.inspectionAirConditioningAndVentilationSystems =
      new InspectionAirConditioningAndVentilationSystems(
        this.entity.inspectionAirConditioningAndVentilationSystems || undefined
      );
  }

  private setInspectionVentilationSystemAndFireProtection(): void {
    this.inspectionTypes = [
      InspectionTypeEnum.InspectionVentilationSystemAndFireProtection
    ];
    this.entity.inspectionVentilationSystemAndFireProtection =
      new InspectionVentilationSystemAndFireProtection(
        this.entity.inspectionVentilationSystemAndFireProtection || undefined
      );
  }

  private setInspectionPressureSystemInStairwellsCompatibilityCheck(): void {
    this.inspectionTypes = [
      InspectionTypeEnum.InspectionPressureSystemInStairwellsCompatibilityCheck
    ];
    this.entity.inspectionPressureSystemInStairwellsCompatibilityCheck =
      new InspectionPressureSystemInStairwellsCompatibilityCheck(
        this.entity.inspectionPressureSystemInStairwellsCompatibilityCheck ||
          undefined
      );
  }

  private setInspectionIntegration(): void {
    this.inspectionTypes = [InspectionTypeEnum.InspectionIntegration];
    this.entity.inspectionIntegration = new InspectionIntegration(
      this.entity.inspectionIntegration || undefined
    );
  }

  private clearInspections(inspectionKeys: InspectionProperty[] = []): void {
    inspectionKeys.forEach((x) => (this.entity[x] = null));
  }

  private update(): Observable<InspectionDto> {
    this.entity.removeNonApplicable();
    const updatedInspection = { ...this.entity };
    delete updatedInspection.order;

    const subject: Observable<InspectionDto> = this.initInspectionInProgress
      ? this.service.replaceOne(updatedInspection.id, updatedInspection)
      : this.service.updateOne(updatedInspection.id, updatedInspection);

    return subject.pipe(tap((resp) => this.onUpdateSuccess(resp)));
  }

  protected onUpdateSuccess = (resp: InspectionDto): void => {
    this.orderDetailsChanged = false;
    this.initInspectionInProgress = false;

    this.clearCache();
    this.onSuccess(resp);
  };

  protected markAsPristine(): void {
    this.dataValChanged(false);
  }

  private onUpdateError = (error: HttpErrorResponse): void => {
    if (error?.status !== 409 && error?.status !== 403) {
      this.onError(error);
      return;
    }

    this.submitting = false;
    clearTimeout(this.timeoutRef);
    this.timeoutRef = null;

    const content =
      error.status === 409
        ? 'הבדיקה נשמרה בינתיים מחלון או ממכשיר אחר. השינויים שלך לא נשמרו כדי לא לדרוס את הנתונים העדכניים. יש לטעון את הבדיקה מחדש.'
        : 'סטטוס ההזמנה כבר אינו מאפשר עריכה של הבדיקה, ולכן השינויים לא נשמרו.';

    this.modal.confirm({
      nzClassName: 'inspection-save-refused',
      nzTitle: 'השינויים לא נשמרו',
      nzContent: content,
      nzOkText: 'טען מחדש',
      nzCancelText: 'הישאר בעמוד',
      nzOnOk: () => {
        this.clearCache();
        this.dataValChanged(false);
        window.location.reload();
      }
    });
  };

  /**
   * Restores the cached inspection. Unsaved order edits (planner, contractor,
   * conclusion, status...) come back only when the order itself is unchanged
   * on the server; otherwise the fresh order wins, since saving the cached one
   * would revert whatever changed there.
   */
  private restoreCache(cached: InspectionDto, orderChanged: boolean): void {
    const freshOrder = this.entity.order;
    const orderUnchanged =
      !!cached.order?.updatedAt &&
      new Date(cached.order.updatedAt).getTime() ===
        new Date(freshOrder?.updatedAt).getTime();
    const restoreOrder = orderChanged && orderUnchanged;

    this.entity = new InspectionModel({
      ...cached,
      // A cache written before the version column existed passed the
      // updatedAt check, so it is based on the current server copy.
      version: cached.version ?? this.entity.version,
      order: restoreOrder ? cached.order : freshOrder
    });
    this.orderDetailsChanged = restoreOrder;
    this.dataValChanged(true);

    if (orderChanged && !orderUnchanged) {
      this.modal.warning({
        nzTitle: 'שינויים בפרטי ההזמנה לא שוחזרו',
        nzContent:
          'ההזמנה עודכנה מאז שהשינויים נשמרו מקומית, ולכן נטענו פרטי ההזמנה העדכניים. השינויים בבדיקה עצמה שוחזרו.'
      });
    }
  }

  /** Caches the whole inspection, plus whether the order has unsaved edits. */
  private writeCache(): void {
    // Logout clears shared storage. An open tab must not recreate a draft
    // after that login has ended or another account has signed in.
    if (
      !this.authService.getAuthToken() ||
      this.authService.getUserId() !== String(this.authService.getUser()?.id)
    ) {
      return;
    }
    localStorage.setItem(
      this.cacheKey,
      JSON.stringify({
        ...this.entity,
        [CACHED_ORDER_CHANGED]: this.orderDetailsChanged
      })
    );
  }

  /** Whether a cached copy was taken from the same server version as the loaded one. */
  private isCacheCurrent(cached: InspectionDto): boolean {
    if (cached.version != null && this.entity.version != null) {
      return cached.version === this.entity.version;
    }

    // Caches written before the version column existed only carry updatedAt.
    return (
      !!cached.updatedAt &&
      !!this.entity.updatedAt &&
      new Date(cached.updatedAt).getTime() ===
        new Date(this.entity.updatedAt).getTime()
    );
  }

  private onCreateTemplateSuccess = (resp: InspectionTemplate): void => {
    this.message.create('success', 'הפעולה נשמרה בהצלחה');

    this.router.navigate(['../../inspection-templates', resp.id], {
      relativeTo: this.route
    });
  };

  /** The cached draft, or null; an unreadable entry is removed. */
  private readCache():
    | (InspectionDto & { [CACHED_ORDER_CHANGED]?: boolean })
    | null {
    const raw = localStorage.getItem(this.cacheKey);
    if (!raw) return null;
    try {
      const cached = JSON.parse(raw);
      if (cached && typeof cached === 'object') return cached;
    } catch {
      // fall through
    }
    this.clearCache();
    return null;
  }

  private clearCache(): void {
    localStorage.removeItem(this.cacheKey);
  }

  private get cacheKey(): string {
    return inspectionDraftKey(this.entity.id);
  }

  private isReadonly(): boolean {
    const hasManagePermission = this.appPermissionsService.has(
      PERMISSION_KEYS.INSPECTIONS_MANAGE
    );
    return isInspectionReadonly(
      hasManagePermission,
      this.entity?.order,
      this.authService.getUser()
    );
  }

  private dataValChanged(val: boolean): void {
    if (this.readonly) {
      this.dataChanged = false;
    } else {
      this.dataChanged = val;
    }
  }

  private prepareForPreview() {
    this.entity.inspections.forEach((i) => i?.setAsApplicable());
  }

  private _initInspection(): void {
    this.tabItems.length = 0;
    this.entity.initInspection();
    this.setInspectionType(this.entity.inspectionType);
    this.entity.order = this.entity.order || new Order();

    this.entity.order.conclusion = Conclusion.Pass;
    this.initInspectionInProgress = true;
  }

  getInspectionStandardDescription(): string {
    let inspectionTypeKey = Object.keys(InspectionTypeEnum).find(
      (k) => InspectionTypeEnum[k] === this.entity.inspectionType
    );

    if (!inspectionTypeKey) {
      return null;
    }

    inspectionTypeKey =
      inspectionTypeKey.charAt(0).toLowerCase() + inspectionTypeKey.slice(1);

    return InspectionStandardDescriptionMap[inspectionTypeKey];
  }
}
